import React, { Fragment, useState, MouseEvent } from 'react';
import { FormattedMessage, FormattedNumber } from 'react-intl';

import Button from 'components/Shared/Button/Button';
import FormattedDateNoTimeZone from 'components/Shared/FormattedDateNoTimeZone/FormattedDateNoTimeZone';
import { GreenSuccessIcon, Declined } from 'components/Shared/Icons/Icons';
import Spinner from 'components/Shared/Spinner/Spinner';
import StatusCardWithFooter from 'components/Shared/StatusCard/StatusCardWithFooter/StatusCardWithFooter';
import { getTxnTextBasedOnType } from 'shared/utils';
import { colors, fontSize, breakpoints } from 'styles/cp';
import { StatusCardType } from 'types/constants';

export type Props = {
  type: StatusCardType;
  companyName: string;
  acceptedDate?: string | Date;
  total?: number;
  currency: string;
  companyLocale: string;
  isPDFError: boolean;
  title?: React.ReactNode;
  message?: React.ReactNode;
  isLoadingEstimate: boolean;
  viewEstimateHandler: () => void;
  isChangeOrder?: boolean;
};

const UnifiedStatusCard = (props: Props) => {
  const [isLoadingAction, setIsLoadingAction] = useState(false);
  const {
    type,
    companyName,
    acceptedDate,
    total,
    currency,
    companyLocale,
    isPDFError,
    title,
    message,
    isLoadingEstimate,
    viewEstimateHandler,
    isChangeOrder,
  } = props;
  const { transaction_text, Transaction_text } = getTxnTextBasedOnType(isChangeOrder);
  const transaction = (
    <FormattedMessage
      id={transaction_text}
      defaultMessage={transaction_text}
      values={{ transaction: transaction_text }}
    />
  );
  const Transaction = (
    <FormattedMessage
      id={Transaction_text}
      defaultMessage={Transaction_text}
      values={{ Transaction: Transaction_text }}
    />
  );

  const viewEstimateClickHandler = (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsLoadingAction(true);
    viewEstimateHandler();
  };

  const isLoadingVisible = isLoadingAction && isLoadingEstimate;

  let actionButtonContent = (
    <FormattedMessage
      id="VIEW_ESTIMATE"
      defaultMessage="View {transaction}"
      values={{ transaction }}
    />
  );

  if (isPDFError) {
    actionButtonContent = (
      <FormattedMessage
        id="ESTIMATE_UNAVAILABLE"
        defaultMessage="{Transaction} unavailable"
        values={{ Transaction }}
      />
    );
  }

  const isAccepted = type === StatusCardType.ACCEPTED;
  const Icon = isAccepted ? GreenSuccessIcon : Declined;

  return (
    <Fragment>
      <StatusCardWithFooter success={isAccepted} return={true} companyLocale={companyLocale}>
        <div className="wrapper">
          <div className="icon-wrapper">
            <Icon />
          </div>
          {title && <div className="title-wrapper">{title}</div>}
          <div className="body">
            {total && (
              <div className="total">
                <FormattedNumber value={total} style="currency" currency={currency} />
              </div>
            )}
            <div className="business">{companyName}</div>
            {acceptedDate && (
              <div className="date">
                <FormattedDateNoTimeZone value={acceptedDate} />
              </div>
            )}
            {message && <div className="message">{message}</div>}
            <div className="mobile-view-estimate">
              <Button
                onClick={viewEstimateClickHandler}
                disabled={isLoadingVisible || isPDFError}
                aria-label="View estimate"
                width="70%"
                data-testid="view-estimate-button-mobile"
              >
                {isLoadingVisible && (
                  <Spinner
                    color={colors.gray04}
                    label={''}
                    bgStrokeWidth={2.5}
                    width={25}
                    strokeWidth={5}
                    bgColor={colors.gray08}
                  />
                )}
                {!isLoadingVisible && actionButtonContent}
              </Button>
            </div>
          </div>
        </div>
      </StatusCardWithFooter>
      <style jsx>{`
        .wrapper {
          font-size: ${fontSize.ml};
          color: ${colors.gray};
          text-align: center;
          font-family: AvenirNextforINTUIT-Medium;
          margin: 40px 0px;
        }

        .icon-wrapper {
          display: flex;
          justify-content: center;
          margin-bottom: 32px;
        }

        .title-wrapper {
          font-family: AvenirNextforINTUIT-Regular;
        }

        .body {
          display: block;
          text-align: center;
        }

        .total {
          font-size: ${fontSize.lg};
          font-family: AvenirNextforINTUIT-Demi;
          line-height: 36px;
          margin-top: 24px;
        }

        .business {
          font-size: ${fontSize.sm};
          font-family: AvenirNextforINTUIT-Demi;
          line-height: 24px;
          margin-top: 16px;
        }

        .date {
          font-size: ${fontSize.xs};
          font-family: AvenirNextforINTUIT-Medium;
          line-height: 20px;
          margin-top: 4px;
        }

        .mobile-view-estimate {
          display: none;
          @media screen and (max-width: ${breakpoints.md}) {
            display: block;
            margin-top: 50px;
          }
        }

        .message {
          font-family: AvenirNextforINTUIT-Regular;
          font-size: ${fontSize.xxs};
          text-align: center;
          margin: 16px 0;
          color: ${colors.ghostGray};

          :global(a) {
            color: ${colors.blue};
            text-decoration: none;
            cursor: pointer;
          }
        }
      `}</style>
    </Fragment>
  );
};

export default UnifiedStatusCard;
